/**
 * Signed Documents Component
 * 
 * Zeigt und verwaltet gescannte/signierte PDFs für einen Vertrag
 * Mit Drag-and-Drop, Mobile-Scan und Dokumentenscanner Unterstützung
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { signedDocumentsApi } from '../services/api';
import { SignedDocument } from '../types';
import DocumentScanner from './DocumentScanner';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  AlertCircle,
  CheckCircle,
  QrCode,
  X,
  FileUp,
  Loader2,
  Eye,
  Printer,
  ScanLine
} from 'lucide-react';

interface SignedDocumentsProps {
  contractId: string;
  canUpload?: boolean;
  onDocumentAdded?: () => void;
}

export default function SignedDocuments({ 
  contractId, 
  canUpload = true,
  onDocumentAdded 
}: SignedDocumentsProps) {
  const [documents, setDocuments] = useState<SignedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ filename: string; status: 'uploading' | 'success' | 'error'; message?: string }[]>([]);
  const [previewDocument, setPreviewDocument] = useState<SignedDocument | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      const { documents } = await signedDocumentsApi.getByContract(contractId);
      setDocuments(documents);
    } catch (err: any) {
      setError('Fehler beim Laden der Dokumente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canUpload) {
      setIsDragging(true);
    }
  }, [canUpload]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!canUpload) return;

    const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
    if (files.length > 0) {
      await uploadFiles(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUpload, contractId]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadFiles(Array.from(files));
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const uploadFiles = async (files: File[]) => {
    setError('');
    setSuccessMessage('');
    
    const progress = files.map(f => ({ filename: f.name, status: 'uploading' as const }));
    setUploadProgress(progress);

    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        // Use uploadWithScan for automatic QR code detection
        const result = await signedDocumentsApi.uploadWithScan(file, contractId);
        
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { 
            ...p, 
            status: 'success',
            message: result.matchedByQr ? 'QR erkannt' : 'Hochgeladen'
          } : p
        ));
        
        successCount++;
      } catch (err: any) {
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'error', message: err.response?.data?.error || 'Fehler' } : p
        ));
      }
    }

    if (successCount > 0) {
      setSuccessMessage(`${successCount} Dokument(e) hochgeladen`);
      await loadDocuments();
      onDocumentAdded?.();
    }

    // Progress nach 3 Sekunden ausblenden
    setTimeout(() => setUploadProgress([]), 3000);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Dokument wirklich löschen?')) return;

    try {
      await signedDocumentsApi.delete(id);
      setDocuments(documents.filter(d => d.id !== id));
      setSuccessMessage('Dokument gelöscht');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const handlePrint = (doc: SignedDocument) => {
    const printUrl = signedDocumentsApi.getViewUrl(doc.id);
    const printWindow = window.open(printUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // Dokumentenscan abgeschlossen
  const handleScanComplete = async (pdfBlob: Blob, detectedContractId: string | null) => {
    setShowScanner(false);
    setError('');
    setSuccessMessage('');
    
    // Verwende erkannten Contract ID oder den aktuellen
    const targetContractId = detectedContractId || contractId;
    
    setUploadProgress([{ filename: 'Gescanntes Dokument.pdf', status: 'uploading' }]);
    
    try {
      const file = new File([pdfBlob], `scan-${Date.now()}.pdf`, { type: 'application/pdf' });
      await signedDocumentsApi.uploadWithScan(file, targetContractId);
      
      setUploadProgress([{ 
        filename: 'Gescanntes Dokument.pdf', 
        status: 'success',
        message: detectedContractId ? 'QR erkannt & zugeordnet' : 'Hochgeladen'
      }]);
      
      setSuccessMessage(
        detectedContractId && detectedContractId !== contractId
          ? `Dokument per QR-Code Vertrag ${detectedContractId.substring(0, 8)}... zugeordnet`
          : 'Dokument erfolgreich gescannt und hochgeladen'
      );
      
      await loadDocuments();
      onDocumentAdded?.();
    } catch (err: any) {
      setUploadProgress([{ 
        filename: 'Gescanntes Dokument.pdf', 
        status: 'error',
        message: err.response?.data?.error || 'Upload fehlgeschlagen'
      }]);
      setError('Fehler beim Hochladen des gescannten Dokuments');
    }
    
    setTimeout(() => setUploadProgress([]), 3000);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'FTP': return <span title="Via FTP-Archiv"><QrCode className="h-4 w-4 text-blue-500" /></span>;
      case 'UPLOAD': return <span title="Manueller Upload"><Upload className="h-4 w-4 text-green-500" /></span>;
      case 'SCAN': return <span title="Gescannt"><FileText className="h-4 w-4 text-purple-500" /></span>;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-primary-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <FileUp className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="font-medium text-gray-900">Signierte Dokumente</h3>
          <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
            {documents.length}
          </span>
        </div>
        {canUpload && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowScanner(true)}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
              title="Dokument scannen (mit Kantenerkennung)"
            >
              <ScanLine className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Scannen</span>
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100"
            >
              <Upload className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Hochladen</span>
            </button>
          </div>
        )}
      </div>

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="application/pdf,image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Messages */}
      {error && (
        <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700 text-sm">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="m-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700 text-sm">
          <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          {successMessage}
          <button onClick={() => setSuccessMessage('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mx-4 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="space-y-2">
            {uploadProgress.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                {item.status === 'uploading' && <Loader2 className="h-4 w-4 text-primary-500 animate-spin" />}
                {item.status === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
                {item.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
                <span className="truncate flex-1">{item.filename}</span>
                {item.message && (
                  <span className={`text-xs ${item.status === 'error' ? 'text-red-500' : 'text-gray-500'}`}>
                    {item.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drag & Drop Zone - Always visible when canUpload */}
      {canUpload && (
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`mx-4 mb-4 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
          <p className="text-sm font-medium text-gray-700">
            PDF-Dateien hier ablegen
          </p>
          <p className="text-xs text-gray-500 mt-1">
            oder Button oben nutzen
          </p>
          <div className="flex items-center justify-center gap-2 mt-2 text-xs text-gray-400">
            <QrCode className="h-3 w-3" />
            QR-Code wird automatisch erkannt
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <div className="p-6 text-center">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">
            Noch keine signierten Dokumente
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {documents.map((doc) => (
            <div key={doc.id} className="p-4 hover:bg-gray-50 flex items-center">
              <div className="flex-shrink-0 mr-3">
                {getSourceIcon(doc.sourceType)}
              </div>
              
              <div className="flex-grow min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {doc.originalName}
                </p>
                <div className="flex items-center text-xs text-gray-500 mt-1 space-x-3">
                  <span>{formatFileSize(doc.fileSize)}</span>
                  <span>{doc.pageCount} Seite(n)</span>
                  <span>{new Date(doc.createdAt).toLocaleDateString('de-AT')}</span>
                  {doc.matchedByQr && (
                    <span className="inline-flex items-center text-purple-600">
                      <QrCode className="h-3 w-3 mr-0.5" />
                      QR
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-1 ml-4">
                <button
                  onClick={() => setPreviewDocument(doc)}
                  className="p-2 text-gray-400 hover:text-primary-600 rounded"
                  title="Anzeigen"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <a
                  href={signedDocumentsApi.getDownloadUrl(doc.id)}
                  className="p-2 text-gray-400 hover:text-primary-600 rounded"
                  title="Herunterladen"
                >
                  <Download className="h-4 w-4" />
                </a>
                <button
                  onClick={() => handlePrint(doc)}
                  className="p-2 text-gray-400 hover:text-primary-600 rounded"
                  title="Drucken"
                >
                  <Printer className="h-4 w-4" />
                </button>
                {canUpload && (
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded"
                    title="Löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Code Info */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-start text-xs text-gray-500">
          <QrCode className="h-4 w-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-600">Automatische Zuordnung via QR-Code</p>
            <p className="mt-1">
              Jede PDF-Seite enthält einen QR-Code. Hochgeladene Dokumente werden automatisch zugeordnet.
            </p>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-[95vw] h-[90vh] max-w-5xl flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{previewDocument.originalName}</h3>
                <p className="text-sm text-gray-500">
                  {formatFileSize(previewDocument.fileSize)} · {previewDocument.pageCount} Seite(n)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={signedDocumentsApi.getDownloadUrl(previewDocument.id)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </a>
                <button
                  onClick={() => handlePrint(previewDocument)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Drucken
                </button>
                <button
                  onClick={() => setPreviewDocument(null)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-100">
              <iframe
                src={signedDocumentsApi.getViewUrl(previewDocument.id)}
                className="w-full h-full border-0"
                title="PDF Vorschau"
              />
            </div>
          </div>
        </div>
      )}

      {/* Document Scanner Modal */}
      {showScanner && (
        <DocumentScanner
          contractId={contractId}
          onComplete={handleScanComplete}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}

// Bulk Upload Component für Admin
export function BulkSignedDocumentsUpload() {
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilesSelected = (fileList: FileList | null) => {
    if (fileList) {
      setFiles(Array.from(fileList));
      setResults([]);
    }
  };

  const handleBulkUpload = async () => {
    if (files.length === 0) return;

    setIsProcessing(true);
    try {
      const { results: uploadResults } = await signedDocumentsApi.bulkUpload(files);
      setResults(uploadResults);
      setFiles([]);
    } catch (err: any) {
      console.error('Bulk upload error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Massen-Upload signierter Dokumente
      </h3>

      <div
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary-400 transition-colors"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-600">
          Mehrere PDF-Dateien auswählen
        </p>
        <p className="text-xs text-gray-400 mt-2">
          Dateien werden anhand des QR-Codes oder Dateinamens zugeordnet
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {files.length} Datei(en) ausgewählt:
          </p>
          <ul className="text-sm text-gray-600 max-h-32 overflow-y-auto space-y-1">
            {files.map((f, i) => (
              <li key={i} className="flex items-center">
                <FileText className="h-4 w-4 mr-2 text-gray-400" />
                {f.name}
              </li>
            ))}
          </ul>
          <button
            onClick={handleBulkUpload}
            disabled={isProcessing}
            className="mt-4 w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isProcessing ? 'Verarbeite...' : 'Alle hochladen'}
          </button>
        </div>
      )}

      {results.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Ergebnisse:</h4>
          <ul className="space-y-2 max-h-48 overflow-y-auto">
            {results.map((r, i) => (
              <li 
                key={i} 
                className={`flex items-center text-sm p-2 rounded ${
                  r.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {r.success ? (
                  <CheckCircle className="h-4 w-4 mr-2" />
                ) : (
                  <AlertCircle className="h-4 w-4 mr-2" />
                )}
                <span className="truncate">{r.filename}</span>
                {r.error && <span className="ml-2 text-xs">({r.error})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
